import { Routes } from '@angular/router';
import { authGuard, changePasswordGuard, guestGuard } from './core/auth/auth.guard';
import { homeGuard, permissionGuard, workspaceLandingRedirect } from './core/auth/permission.guard';
import {
  FEATURE_PERMISSION_MODES,
  FEATURE_PERMISSIONS,
} from './core/auth/feature-permissions';
import { unsavedWorkGuard } from './core/services/unsaved-work.guard';

// Route titles are translation keys; TranslatedTitleStrategy resolves them
// and appends the product name.
export const routes: Routes = [
  {
    path: 'login',
    title: 'title.signIn',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/login').then((m) => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    title: 'title.forgotPassword',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/forgot-password').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    title: 'title.resetPassword',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/auth/reset-password').then((m) => m.ResetPasswordComponent),
  },
  {
    path: 'change-password',
    title: 'title.changePassword',
    canActivate: [changePasswordGuard],
    loadComponent: () =>
      import('./features/auth/change-password').then((m) => m.ChangePasswordComponent),
  },
  {
    // A focused invoice presentation outside the authenticated application
    // shell. The guard still restores the session and enforces the exact
    // billing-print grant, but the document is not squeezed beside navigation
    // or workspace controls.
    path: 'print/invoice/:invoice',
    title: 'title.printing',
    canActivate: [authGuard, permissionGuard],
    data: {
      permissions: ['printing.billing'],
      permissionMode: 'all',
    },
    loadComponent: () =>
      import('./features/printing/print-workspace').then((m) => m.PrintWorkspaceComponent),
  },
  {
    // The admission's invoice on one A4 sheet, outside the shell like the
    // invoice above, so nothing but the sheet reaches the paper.
    path: 'print/admission-invoice/:id',
    title: 'title.admissionInvoicePrint',
    canActivate: [authGuard, permissionGuard],
    data: {
      permissions: FEATURE_PERMISSIONS.admissionInvoice,
      permissionMode: FEATURE_PERMISSION_MODES.admissionInvoice,
    },
    loadComponent: () =>
      import('./features/admissions/admission-invoice-print').then((m) => m.AdmissionInvoicePrintComponent),
  },
  {
    path: '',
    // Lazy, not eager. The shell is the authenticated chrome — its navigation
    // table, ten inline icons and organization switcher are ~26 kB that a
    // visitor at `/login` never renders, and `component:` put all of it in the
    // initial bundle. `loadComponent` moves it behind the same `authGuard`
    // that already decides whether it may be shown, so the chunk is fetched
    // exactly when the guard admits someone and in parallel with the child
    // route's own chunk. No route declaration, guard, title or child changes.
    loadComponent: () => import('./layout/shell').then((m) => m.ShellComponent),
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: workspaceLandingRedirect },
      {
        // «الرئيسية» (the owner, 2026-10-13): the desk's shortcuts, as cards,
        // and the first page after signing in for whoever has one of them.
        path: 'home',
        title: 'title.home',
        canActivate: [homeGuard],
        loadComponent: () => import('./features/home/home-page').then((m) => m.HomePageComponent),
      },
      {
        path: 'change-pin',
        title: 'title.changePin',
        loadComponent: () =>
          import('./features/auth/change-pin').then((m) => m.ChangePinComponent),
      },
      {
        path: 'forbidden',
        title: 'title.forbidden',
        loadComponent: () =>
          import('./features/errors/access-denied').then((m) => m.AccessDeniedComponent),
      },
      {
        path: 'dashboard',
        pathMatch: 'full',
        redirectTo: 'operations/dashboards',
      },
      {
        path: 'printing',
        title: 'title.printing',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.printing,
          permissionMode: FEATURE_PERMISSION_MODES.printing,
        },
        loadComponent: () =>
          import('./features/printing/print-workspace').then((m) => m.PrintWorkspaceComponent),
      },
      {
        path: 'patients',
        title: 'title.patients',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.patients,
          permissionMode: FEATURE_PERMISSION_MODES.patients,
        },
        loadComponent: () => import('./features/patients/patient-list').then((m) => m.PatientListComponent),
      },
      {
        // Before the `:id` route on purpose: registered after it, "register"
        // would resolve as a patient identifier and 404.
        path: 'patients/register',
        title: 'title.registerPatient',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.patientRegistration,
          permissionMode: FEATURE_PERMISSION_MODES.patientRegistration,
        },
        loadComponent: () =>
          import('./features/patients/patient-register').then((m) => m.PatientRegisterComponent),
      },
      {
        path: 'patients/:id',
        title: 'title.patientRecord',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.patients,
          permissionMode: FEATURE_PERMISSION_MODES.patients,
        },
        loadComponent: () => import('./features/patients/patient-detail').then((m) => m.PatientDetailComponent),
      },
      {
        // Before the `:id` route on purpose: registered after it, "new" would
        // resolve as an admission identifier and 404.
        path: 'admissions/new',
        title: 'title.createAdmission',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissionCreate,
          permissionMode: FEATURE_PERMISSION_MODES.admissionCreate,
        },
        loadComponent: () =>
          import('./features/admissions/admission-create').then((m) => m.AdmissionCreateComponent),
      },
      {
        // «الأطفال في المركز» (2026-10-07). Before the `:id` routes on purpose,
        // like "new": after them, "in-center" would resolve as an admission.
        path: 'admissions/in-center',
        title: 'title.admissionsInCenter',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissionsInCenter,
          permissionMode: FEATURE_PERMISSION_MODES.admissionsInCenter,
        },
        loadComponent: () =>
          import('./features/admissions/admissions-in-center').then((m) => m.AdmissionsInCenterComponent),
      },
      {
        // «الأطباء المحوّلين» (2026-10-20). Before the `:id` routes, like "in-center".
        path: 'admissions/referring-doctors',
        title: 'title.referringDoctors',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.referringDoctors, permissionMode: FEATURE_PERMISSION_MODES.referringDoctors },
        loadComponent: () => import('./features/admissions/referring-doctors').then((m) => m.ReferringDoctorsComponent),
      },
      {
        // «فواتير الدخول» (2026-10-14): every admission's invoice, the closed
        // ones too. Before the `:id` routes, like "in-center".
        path: 'admissions/invoices',
        title: 'title.admissionInvoiceList',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissionInvoiceList,
          permissionMode: FEATURE_PERMISSION_MODES.admissionInvoiceList,
        },
        loadComponent: () =>
          import('./features/admissions/admission-invoice-list').then((m) => m.AdmissionInvoiceListComponent),
      },
      {
        path: 'admissions/:id/invoice',
        title: 'title.admissionInvoice',
        canActivate: [permissionGuard],
        // Typed work is not dropped by a link or the back button (2026-10-07).
        canDeactivate: [unsavedWorkGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissionInvoice,
          permissionMode: FEATURE_PERMISSION_MODES.admissionInvoice,
        },
        loadComponent: () =>
          import('./features/admissions/admission-invoice-page').then((m) => m.AdmissionInvoicePageComponent),
      },
      {
        path: 'admissions/:id',
        title: 'title.admission',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissions,
          permissionMode: FEATURE_PERMISSION_MODES.admissions,
        },
        loadComponent: () =>
          import('./features/admissions/admission-detail').then((m) => m.AdmissionDetailComponent),
      },
      {
        // Before the `:id` route, and namespaced under the patient because an
        // encounter is always opened *for* someone.
        path: 'patients/:patientId/clinical-encounters/new',
        title: 'title.createEncounter',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.clinicalEncounterCreate,
          permissionMode: FEATURE_PERMISSION_MODES.clinicalEncounterCreate,
        },
        loadComponent: () =>
          import('./features/clinical/encounter-create').then((m) => m.EncounterCreateComponent),
      },
      {
        // Namespaced under the patient because a 360 view is a statement about
        // one child. `patients.view` opens it; each section inside is withheld
        // or returned by the server according to its own clinical grant, so
        // listing those grants here would refuse the whole screen to a caller
        // entitled to part of it.
        path: 'patients/:patientId/summary',
        title: 'title.patientSummary',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.patientSummary,
          permissionMode: FEATURE_PERMISSION_MODES.patientSummary,
        },
        loadComponent: () =>
          import('./features/clinical/patient-summary').then((m) => m.PatientSummaryComponent),
      },
      {
        path: 'laboratory/tests',
        title: 'title.laboratoryCatalogue',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.laboratoryCatalogue,
          permissionMode: FEATURE_PERMISSION_MODES.laboratoryCatalogue,
        },
        loadComponent: () =>
          import('./features/diagnostics/laboratory-catalogue').then((m) => m.LaboratoryCatalogueComponent),
      },
      {
        path: 'laboratory/worklist',
        title: 'title.laboratoryWorklist',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.laboratoryWorklist,
          permissionMode: FEATURE_PERMISSION_MODES.laboratoryWorklist,
        },
        loadComponent: () =>
          import('./features/diagnostics/laboratory-worklist').then((m) => m.LaboratoryWorklistComponent),
      },
      {
        path: 'radiology/services',
        title: 'title.radiologyCatalogue',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.radiologyCatalogue,
          permissionMode: FEATURE_PERMISSION_MODES.radiologyCatalogue,
        },
        loadComponent: () =>
          import('./features/diagnostics/radiology-catalogue').then((m) => m.RadiologyCatalogueComponent),
      },
      {
        path: 'radiology/worklist',
        title: 'title.radiologyWorklist',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.radiologyWorklist,
          permissionMode: FEATURE_PERMISSION_MODES.radiologyWorklist,
        },
        loadComponent: () =>
          import('./features/diagnostics/radiology-worklist').then((m) => m.RadiologyWorklistComponent),
      },
      {
        path: 'diagnostics/critical-alerts',
        title: 'title.criticalAlerts',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.criticalAlerts,
          permissionMode: FEATURE_PERMISSION_MODES.criticalAlerts,
        },
        loadComponent: () =>
          import('./features/diagnostics/critical-alerts').then((m) => m.CriticalAlertsComponent),
      },
      {
        path: 'inventory/items/new',
        title: 'title.addCatalogItem',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.catalogItemCreate,
          permissionMode: FEATURE_PERMISSION_MODES.catalogItemCreate,
        },
        loadComponent: () =>
          import('./features/inventory/catalog-item-create').then((m) => m.CatalogItemCreateComponent),
      },
      {
        path: 'inventory/items',
        title: 'title.itemsCatalogue',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.itemsCatalogue,
          permissionMode: FEATURE_PERMISSION_MODES.itemsCatalogue,
        },
        loadComponent: () =>
          import('./features/inventory/items-catalogue').then((m) => m.ItemsCatalogueComponent),
      },
      {
        path: 'inventory/consumables',
        title: 'title.consumables',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.consumables,
          permissionMode: FEATURE_PERMISSION_MODES.consumables,
        },
        loadComponent: () =>
          import('./features/inventory/consumables-catalogue').then((m) => m.ConsumablesCatalogueComponent),
      },
      {
        path: 'inventory/consumables/:id',
        title: 'title.consumable',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.consumables,
          permissionMode: FEATURE_PERMISSION_MODES.consumables,
        },
        loadComponent: () =>
          import('./features/inventory/consumable-detail').then((m) => m.ConsumableDetailComponent),
      },
      {
        path: 'pharmacy/medications',
        title: 'title.pharmacyCatalogue',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyCatalogue,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyCatalogue,
        },
        loadComponent: () =>
          import('./features/pharmacy/medication-catalogue').then((m) => m.MedicationCatalogueComponent),
      },
      {
        path: 'pharmacy/orders',
        title: 'title.pharmacyWorklist',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyOrders,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyOrders,
        },
        loadComponent: () =>
          import('./features/pharmacy/pharmacy-worklist').then((m) => m.PharmacyWorklistComponent),
      },
      {
        path: 'inventory/merge-items',
        title: 'title.inventoryMergeItems',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryMergeItems,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryMergeItems,
        },
        loadComponent: () =>
          import('./features/inventory/merge-items/merge-items-page').then((m) => m.MergeItemsPageComponent),
      },
      {
        path: 'inventory/reorder-levels',
        title: 'title.inventoryReorderLevels',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryReorderLevels,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryReorderLevels,
        },
        loadComponent: () =>
          import('./features/inventory/reorder-levels/reorder-levels-page').then((m) => m.ReorderLevelsPageComponent),
      },
      {
        path: 'inventory/stock-count',
        title: 'title.inventoryStockCount',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryStockCount,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryStockCount,
        },
        loadComponent: () =>
          import('./features/inventory/stock-count/stock-count-log').then((m) => m.StockCountLogComponent),
      },
      {
        path: 'inventory/stock-count/:id',
        title: 'title.inventoryStockCount',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryStockCount,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryStockCount,
        },
        loadComponent: () =>
          import('./features/inventory/stock-count/stock-count-page').then((m) => m.StockCountPageComponent),
      },
      {
        path: 'inventory/sale-prices',
        title: 'title.inventorySalePrices',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventorySalePrices,
          permissionMode: FEATURE_PERMISSION_MODES.inventorySalePrices,
        },
        loadComponent: () =>
          import('./features/inventory/inventory-sale-prices').then((m) => m.InventorySalePricesComponent),
      },
      {
        path: 'inventory/ledger',
        title: 'title.inventoryLedger',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryLedger,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryLedger,
        },
        loadComponent: () =>
          import('./features/inventory/inventory-ledger').then((m) => m.InventoryLedgerComponent),
      },
      {
        path: 'inventory/packaging',
        title: 'title.inventoryPackaging',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryPackaging,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryPackaging,
        },
        loadComponent: () =>
          import('./features/inventory/inventory-packaging-settings').then((m) => m.InventoryPackagingSettingsComponent),
      },
      {
        path: 'inventory/approvals',
        title: 'title.inventoryApprovals',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.inventoryApprovals,
          permissionMode: FEATURE_PERMISSION_MODES.inventoryApprovals,
        },
        loadComponent: () =>
          import('./features/inventory/inventory-approvals').then((m) => m.InventoryApprovalsComponent),
      },
      {
        path: 'pharmacy/dispenses',
        title: 'title.pharmacyDispenses',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyDispenses,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyDispenses,
        },
        loadComponent: () =>
          import('./features/pharmacy/dispense-worklist').then((m) => m.DispenseWorklistComponent),
      },
      {
        path: 'pharmacy/dispenses/:id',
        title: 'title.pharmacyDispense',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyDispenses,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyDispenses,
        },
        loadComponent: () =>
          import('./features/pharmacy/dispense-detail').then((m) => m.DispenseDetailComponent),
      },
      {
        path: 'purchasing/invoices',
        title: 'title.purchaseInvoices',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.purchaseInvoices,
          permissionMode: FEATURE_PERMISSION_MODES.purchaseInvoices,
        },
        loadComponent: () =>
          import('./features/purchasing/purchase-invoice-list').then((m) => m.PurchaseInvoiceListComponent),
      },
      {
        // `new` is a literal, so it must be declared before `:id` or the form
        // would try to load an invoice with that identifier.
        path: 'purchasing/invoices/new',
        title: 'title.purchaseInvoiceForm',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.purchaseInvoices,
          permissionMode: FEATURE_PERMISSION_MODES.purchaseInvoices,
        },
        loadComponent: () =>
          import('./features/purchasing/purchase-invoice-form').then((m) => m.PurchaseInvoiceFormComponent),
      },
      {
        path: 'purchasing/invoices/:id',
        title: 'title.purchaseInvoiceForm',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.purchaseInvoices,
          permissionMode: FEATURE_PERMISSION_MODES.purchaseInvoices,
        },
        loadComponent: () =>
          import('./features/purchasing/purchase-invoice-form').then((m) => m.PurchaseInvoiceFormComponent),
      },
      {
        path: 'purchasing/orders',
        title: 'title.purchaseOrders',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.purchaseOrders,
          permissionMode: FEATURE_PERMISSION_MODES.purchaseOrders,
        },
        loadComponent: () =>
          import('./features/purchasing/purchase-order-list').then((m) => m.PurchaseOrderListComponent),
      },
      {
        // Ahead of `purchasing/orders/:id` so the literal segment wins.
        path: 'purchasing/orders/:id/receive',
        title: 'title.goodsReceiptForm',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.goodsReceipts,
          permissionMode: FEATURE_PERMISSION_MODES.goodsReceipts,
        },
        loadComponent: () =>
          import('./features/purchasing/goods-receipt-form').then((m) => m.GoodsReceiptFormComponent),
      },
      {
        path: 'purchasing/orders/:id',
        title: 'title.purchaseOrder',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.purchaseOrders,
          permissionMode: FEATURE_PERMISSION_MODES.purchaseOrders,
        },
        loadComponent: () =>
          import('./features/purchasing/purchase-order-detail').then((m) => m.PurchaseOrderDetailComponent),
      },
      {
        path: 'purchasing/receipts',
        title: 'title.goodsReceipts',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.goodsReceipts,
          permissionMode: FEATURE_PERMISSION_MODES.goodsReceipts,
        },
        loadComponent: () =>
          import('./features/purchasing/goods-receipt-register').then((m) => m.GoodsReceiptRegisterComponent),
      },
      {
        path: 'purchasing/receipts/:id',
        title: 'title.goodsReceipt',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.goodsReceipts,
          permissionMode: FEATURE_PERMISSION_MODES.goodsReceipts,
        },
        loadComponent: () =>
          import('./features/purchasing/goods-receipt-detail').then((m) => m.GoodsReceiptDetailComponent),
      },
      {
        path: 'pharmacy/orders/:orderItemId',
        title: 'title.pharmacyOrder',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyOrders,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyOrders,
        },
        loadComponent: () =>
          import('./features/pharmacy/medication-order-review').then((m) => m.MedicationOrderReviewComponent),
      },
      {
        path: 'pharmacy/medications/:id',
        title: 'title.pharmacyMedication',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.pharmacyCatalogue,
          permissionMode: FEATURE_PERMISSION_MODES.pharmacyCatalogue,
        },
        loadComponent: () =>
          import('./features/pharmacy/medication-detail').then((m) => m.MedicationDetailComponent),
      },
      {
        path: 'clinical-encounters',
        title: 'title.clinicalEncounters',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.clinicalEncounters,
          permissionMode: FEATURE_PERMISSION_MODES.clinicalEncounters,
        },
        loadComponent: () =>
          import('./features/clinical/encounter-worklist').then((m) => m.EncounterWorklistComponent),
      },
      {
        // Identifier-bearing, so it is not a landing destination for any
        // persona: it is reached from an encounter, never fallen back to.
        path: 'clinical-encounters/:encounter/emar',
        title: 'title.emar',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.emar,
          permissionMode: FEATURE_PERMISSION_MODES.emar,
        },
        loadComponent: () =>
          import('./features/emar/emar-workspace').then((m) => m.EmarWorkspaceComponent),
      },
      {
        path: 'clinical-encounters/:id/history',
        title: 'title.clinicalHistory',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.clinicalHistory,
          permissionMode: FEATURE_PERMISSION_MODES.clinicalHistory,
        },
        loadComponent: () =>
          import('./features/clinical/encounter-history').then((m) => m.EncounterHistoryComponent),
      },
      {
        path: 'clinical-encounters/:id/printable-summary',
        title: 'title.printableSummary',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.printableSummary,
          permissionMode: FEATURE_PERMISSION_MODES.printableSummary,
        },
        loadComponent: () =>
          import('./features/clinical/printable-summary').then((m) => m.PrintableSummaryComponent),
      },
      {
        path: 'clinical-encounters/:id',
        title: 'title.clinicalEncounter',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.clinicalEncounters,
          permissionMode: FEATURE_PERMISSION_MODES.clinicalEncounters,
        },
        loadComponent: () =>
          import('./features/clinical/encounter-detail').then((m) => m.EncounterDetailComponent),
      },
      {
        path: 'nursing-handovers/:id',
        title: 'title.nursingHandover',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.nursingHandovers,
          permissionMode: FEATURE_PERMISSION_MODES.nursingHandovers,
        },
        loadComponent: () =>
          import('./features/nursing/handover-detail').then((m) => m.HandoverDetailComponent),
      },
      {
        path: 'nursing-care-plans/:id',
        title: 'title.nursingCarePlan',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.nursingCarePlans,
          permissionMode: FEATURE_PERMISSION_MODES.nursingCarePlans,
        },
        loadComponent: () =>
          import('./features/nursing/care-plan-detail').then((m) => m.CarePlanDetailComponent),
      },
      {
        path: 'nursing-escalations',
        title: 'title.nursingEscalations',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.nursingEscalations,
          permissionMode: FEATURE_PERMISSION_MODES.nursingEscalations,
        },
        loadComponent: () =>
          import('./features/nursing/escalation-worklist').then((m) => m.EscalationWorklistComponent),
      },
      {
        path: 'nursing-assessments/:id',
        title: 'title.nursingAssessment',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.nursingAssessments,
          permissionMode: FEATURE_PERMISSION_MODES.nursingAssessments,
        },
        loadComponent: () =>
          import('./features/nursing/assessment-detail').then((m) => m.AssessmentDetailComponent),
      },
      {
        path: 'clinical-notes/:id',
        title: 'title.clinicalNote',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.clinicalNotes,
          permissionMode: FEATURE_PERMISSION_MODES.clinicalNotes,
        },
        loadComponent: () =>
          import('./features/clinical/note-detail').then((m) => m.NoteDetailComponent),
      },
      {
        path: 'housekeeping',
        title: 'title.housekeeping',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.housekeeping,
          permissionMode: FEATURE_PERMISSION_MODES.housekeeping,
        },
        loadComponent: () =>
          import('./features/housekeeping/housekeeping-queue').then((m) => m.HousekeepingQueueComponent),
      },
      {
        path: 'bed-board',
        title: 'title.bedBoard',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.bedBoard,
          permissionMode: FEATURE_PERMISSION_MODES.bedBoard,
        },
        loadComponent: () =>
          import('./features/admissions/bed-board').then((m) => m.BedBoardComponent),
      },
      {
        path: 'appointments',
        title: 'title.appointments',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.appointments,
          permissionMode: FEATURE_PERMISSION_MODES.appointments,
        },
        loadComponent: () =>
          import('./features/scheduling/appointment-workspace').then(
            (m) => m.AppointmentWorkspaceComponent,
          ),
      },
      {
        path: 'insurance/coverages',
        title: 'title.insuranceCoverages',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.insuranceCoverages,
          permissionMode: FEATURE_PERMISSION_MODES.insuranceCoverages,
        },
        loadComponent: () =>
          import('./features/insurance/coverage-register').then((m) => m.CoverageRegisterComponent),
      },
      {
        path: 'insurance/preauthorizations',
        title: 'title.insurancePreauthorizations',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.insurancePreauthorizations,
          permissionMode: FEATURE_PERMISSION_MODES.insurancePreauthorizations,
        },
        loadComponent: () =>
          import('./features/insurance/preauthorization-register').then(
            (m) => m.PreauthorizationRegisterComponent,
          ),
      },
      {
        path: 'insurance/claims',
        title: 'title.insuranceClaims',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.insuranceClaims,
          permissionMode: FEATURE_PERMISSION_MODES.insuranceClaims,
        },
        loadComponent: () =>
          import('./features/insurance/claim-register').then((m) => m.ClaimRegisterComponent),
      },
      {
        path: 'insurance/remittances',
        title: 'title.insuranceRemittances',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.insuranceRemittances,
          permissionMode: FEATURE_PERMISSION_MODES.insuranceRemittances,
        },
        loadComponent: () =>
          import('./features/insurance/remittance-register').then(
            (m) => m.RemittanceRegisterComponent,
          ),
      },
      {
        path: 'patient-flow',
        title: 'title.patientFlow',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.patientFlow,
          permissionMode: FEATURE_PERMISSION_MODES.patientFlow,
        },
        loadComponent: () =>
          import('./features/scheduling/patient-flow-workspace').then(
            (m) => m.PatientFlowWorkspaceComponent,
          ),
      },
      {
        path: 'alerts',
        pathMatch: 'full',
        redirectTo: 'operations/alerts',
      },
      {
        path: 'operations/alerts',
        title: 'title.alertWorklist',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.alertWorklist,
          permissionMode: FEATURE_PERMISSION_MODES.alertWorklist,
        },
        loadComponent: () =>
          import('./features/operations/alert-worklist').then((m) => m.AlertWorklistComponent),
      },
      {
        path: 'operations/dashboards',
        title: 'title.dashboards',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.dashboards,
          permissionMode: FEATURE_PERMISSION_MODES.dashboards,
        },
        loadComponent: () =>
          import('./features/operations/dashboards').then((m) => m.DashboardsComponent),
      },
      {
        path: 'operations/inbox',
        title: 'title.notificationInbox',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.notificationInbox,
          permissionMode: FEATURE_PERMISSION_MODES.notificationInbox,
        },
        loadComponent: () =>
          import('./features/operations/notification-inbox').then((m) => m.NotificationInboxComponent),
      },
      {
        path: 'operations/notification-preferences',
        title: 'title.notificationPreferences',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.notificationPreferences,
          permissionMode: FEATURE_PERMISSION_MODES.notificationPreferences,
        },
        loadComponent: () =>
          import('./features/operations/notification-preferences').then(
            (m) => m.NotificationPreferencesComponent,
          ),
      },
      {
        path: 'finance/profit-shares',
        title: 'title.profitShares',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.profitShares,
          permissionMode: FEATURE_PERMISSION_MODES.profitShares,
        },
        loadComponent: () =>
          import('./features/finance/profit-share-register').then((m) => m.ProfitShareRegisterComponent),
      },
      {
        path: 'partner-portal',
        title: 'title.partnerPortal',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.partnerPortal,
          permissionMode: FEATURE_PERMISSION_MODES.partnerPortal,
        },
        loadComponent: () =>
          import('./features/finance/partner-portal').then((m) => m.PartnerPortalComponent),
      },
      {
        // «التقارير» (2026-10-18): a coloured square for each report, as Elbasha's are.
        path: 'reports',
        title: 'title.reports',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.reports, permissionMode: FEATURE_PERMISSION_MODES.reports },
        loadComponent: () => import('./features/reports/reports-home').then((m) => m.ReportsHomeComponent),
      },
      {
        path: 'reports/day-summary',
        title: 'title.daySummaryReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.daySummaryReport, permissionMode: FEATURE_PERMISSION_MODES.daySummaryReport },
        loadComponent: () => import('./features/reports/day-summary-report').then((m) => m.DaySummaryReportComponent),
      },
      {
        path: 'reports/case-profit',
        title: 'title.caseProfitReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.caseProfitReport, permissionMode: FEATURE_PERMISSION_MODES.caseProfitReport },
        loadComponent: () => import('./features/reports/case-profit-report').then((m) => m.CaseProfitReportComponent),
      },
      {
        path: 'reports/month-profit',
        title: 'title.monthProfitReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.monthProfitReport, permissionMode: FEATURE_PERMISSION_MODES.monthProfitReport },
        loadComponent: () => import('./features/reports/month-profit-report').then((m) => m.MonthProfitReportComponent),
      },
      {
        path: 'reports/stock-value',
        title: 'title.stockValueReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.stockValueReport, permissionMode: FEATURE_PERMISSION_MODES.stockValueReport },
        loadComponent: () => import('./features/reports/stock-value-report').then((m) => m.StockValueReportComponent),
      },
        // The rest of «التقارير» (2026-10-20).
      {
        path: 'reports/payments',
        title: 'title.paymentsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/money-reports').then((m) => m.PaymentsReportComponent),
      },
      {
        path: 'reports/revenue',
        title: 'title.revenueReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/revenue-report').then((m) => m.RevenueReportComponent),
      },
      {
        path: 'reports/balances',
        title: 'title.balancesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/money-reports').then((m) => m.BalancesReportComponent),
      },
      {
        path: 'reports/discounts',
        title: 'title.discountsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/money-reports').then((m) => m.DiscountsReportComponent),
      },
      {
        path: 'reports/invoice-edits',
        title: 'title.invoiceEditsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/money-reports').then((m) => m.InvoiceEditsReportComponent),
      },
      {
        path: 'reports/expenses',
        title: 'title.expensesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.moneyReports, permissionMode: FEATURE_PERMISSION_MODES.moneyReports },
        loadComponent: () => import('./features/reports/money-reports').then((m) => m.ExpensesReportComponent),
      },
      {
        path: 'reports/referrals',
        title: 'title.referralsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.referralReports, permissionMode: FEATURE_PERMISSION_MODES.referralReports },
        loadComponent: () => import('./features/reports/referral-reports').then((m) => m.ReferralsReportComponent),
      },
      {
        path: 'reports/referrals/:id',
        title: 'title.referralStatement',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.referralReports, permissionMode: FEATURE_PERMISSION_MODES.referralReports },
        loadComponent: () => import('./features/reports/referral-reports').then((m) => m.ReferralStatementComponent),
      },
      {
        path: 'reports/admissions-flow',
        title: 'title.admissionsFlowReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.operationsReports, permissionMode: FEATURE_PERMISSION_MODES.operationsReports },
        loadComponent: () => import('./features/reports/operations-reports').then((m) => m.AdmissionsFlowReportComponent),
      },
      {
        path: 'reports/occupancy',
        title: 'title.occupancyReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.operationsReports, permissionMode: FEATURE_PERMISSION_MODES.operationsReports },
        loadComponent: () => import('./features/reports/operations-reports').then((m) => m.OccupancyReportComponent),
      },
      {
        path: 'reports/attending-cases',
        title: 'title.attendingCasesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.attendingCasesReport, permissionMode: FEATURE_PERMISSION_MODES.attendingCasesReport },
        loadComponent: () => import('./features/reports/operations-reports').then((m) => m.AttendingCasesReportComponent),
      },
      {
        path: 'reports/examinations',
        title: 'title.examinationsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.operationsReports, permissionMode: FEATURE_PERMISSION_MODES.operationsReports },
        loadComponent: () => import('./features/reports/operations-reports').then((m) => m.ExaminationsReportComponent),
      },
      {
        path: 'reports/low-stock',
        title: 'title.lowStockReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.lowStockReport, permissionMode: FEATURE_PERMISSION_MODES.lowStockReport },
        loadComponent: () => import('./features/reports/operations-reports').then((m) => m.LowStockReportComponent),
      },
      {
        path: 'reports/user-activity',
        title: 'title.userActivityReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.userActivityReport, permissionMode: FEATURE_PERMISSION_MODES.userActivityReport },
        loadComponent: () => import('./features/reports/user-activity-report').then((m) => m.UserActivityReportComponent),
      },
      {
        path: 'reports/expiry',
        title: 'title.expiryReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.storeReports, permissionMode: FEATURE_PERMISSION_MODES.storeReports },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.ExpiryReportComponent),
      },
      {
        path: 'reports/consumables',
        title: 'title.consumablesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.storeReports, permissionMode: FEATURE_PERMISSION_MODES.storeReports },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.ConsumablesReportComponent),
      },
      {
        path: 'reports/purchases',
        title: 'title.purchasesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.purchasesReport, permissionMode: FEATURE_PERMISSION_MODES.purchasesReport },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.PurchasesReportComponent),
      },
      {
        path: 'reports/count-results',
        title: 'title.countResultsReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.countResultsReport, permissionMode: FEATURE_PERMISSION_MODES.countResultsReport },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.CountResultsReportComponent),
      },
      {
        path: 'reports/nurse-shares',
        title: 'title.nurseSharesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.nurseSharesReport, permissionMode: FEATURE_PERMISSION_MODES.nurseSharesReport },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.NurseSharesReportComponent),
      },
      {
        path: 'reports/attendance-changes',
        title: 'title.attendanceChangesReport',
        canActivate: [permissionGuard],
        data: { permissions: FEATURE_PERMISSIONS.attendanceChangesReport, permissionMode: FEATURE_PERMISSION_MODES.attendanceChangesReport },
        loadComponent: () => import('./features/reports/store-staff-reports').then((m) => m.AttendanceChangesReportComponent),
      },
      {
        path: 'reports/exports',
        title: 'title.reportExports',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.reportExports,
          permissionMode: FEATURE_PERMISSION_MODES.reportExports,
        },
        loadComponent: () =>
          import('./features/reports/export-workspace').then((m) => m.ExportWorkspaceComponent),
      },
      {
        path: 'finance',
        title: 'title.finance',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.finance,
          permissionMode: FEATURE_PERMISSION_MODES.finance,
        },
        loadComponent: () => import('./features/finance/finance-overview').then((m) => m.FinanceOverviewComponent),
      },
      {
        path: 'finance/cash-tills',
        title: 'title.cashTills',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.cashTills,
          permissionMode: FEATURE_PERMISSION_MODES.cashTills,
        },
        loadComponent: () => import('./features/finance/cash-till-workspace').then((m) => m.CashTillWorkspaceComponent),
      },
      {
        path: 'finance/daily-cash',
        title: 'title.dailyCash',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.cashTills,
          permissionMode: FEATURE_PERMISSION_MODES.cashTills,
        },
        loadComponent: () => import('./features/finance/till-movements-report').then((m) => m.TillMovementsReportComponent),
      },
      {
        path: 'finance/dashboard',
        title: 'title.financeDashboard',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.financeDashboard,
          permissionMode: FEATURE_PERMISSION_MODES.financeDashboard,
        },
        loadComponent: () => import('./features/finance/finance-dashboard').then((m) => m.FinanceDashboardComponent),
      },
      {
        path: 'finance/treasury-dashboard',
        title: 'title.treasuryDashboard',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.ownerTreasuryDashboard,
          permissionMode: FEATURE_PERMISSION_MODES.ownerTreasuryDashboard,
        },
        loadComponent: () => import('./features/finance/owner-treasury-dashboard').then((m) => m.OwnerTreasuryDashboardComponent),
      },
      {
        path: 'finance/expenses',
        title: 'title.expenses',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.expenses,
          permissionMode: FEATURE_PERMISSION_MODES.expenses,
        },
        loadComponent: () => import('./features/finance/expense-workspace').then((m) => m.ExpenseWorkspaceComponent),
      },
      {
        // «المعامل ومراكز الأشعة» (2026-10-12).
        path: 'finance/external-providers',
        title: 'title.externalProviders',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.externalProviders,
          permissionMode: FEATURE_PERMISSION_MODES.externalProviders,
        },
        loadComponent: () =>
          import('./features/finance/external-providers-page').then((m) => m.ExternalProvidersPageComponent),
      },
      {
        path: 'finance/service-pricing',
        title: 'title.servicePricing',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.servicePricing,
          permissionMode: FEATURE_PERMISSION_MODES.servicePricing,
        },
        loadComponent: () =>
          import('./features/finance/service-pricing').then((m) => m.ServicePricingComponent),
      },
      {
        path: 'finance/accommodation-rates',
        title: 'title.accommodationRates',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.accommodationRates,
          permissionMode: FEATURE_PERMISSION_MODES.accommodationRates,
        },
        loadComponent: () =>
          import('./features/finance/accommodation-rates').then((m) => m.AccommodationRatesComponent),
      },
      {
        path: 'finance/ownership',
        title: 'title.ownershipRegister',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.ownershipRegister,
          permissionMode: FEATURE_PERMISSION_MODES.ownershipRegister,
        },
        loadComponent: () =>
          import('./features/finance/ownership-register').then((m) => m.OwnershipRegisterComponent),
      },
      {
        path: 'finance/partners',
        title: 'title.partners',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.partners,
          permissionMode: FEATURE_PERMISSION_MODES.partners,
        },
        loadComponent: () => import('./features/finance/partner-register').then((m) => m.PartnerRegisterComponent),
      },
      {
        path: 'finance/accounting-periods',
        title: 'title.accountingPeriods',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.accountingPeriods,
          permissionMode: FEATURE_PERMISSION_MODES.accountingPeriods,
        },
        loadComponent: () =>
          import('./features/finance/accounting-periods').then((m) => m.AccountingPeriodsComponent),
      },
      {
        path: 'finance/management-fees',
        title: 'title.managementFees',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.managementFeeRules,
          permissionMode: FEATURE_PERMISSION_MODES.managementFeeRules,
        },
        loadComponent: () =>
          import('./features/finance/management-fee-rules').then((m) => m.ManagementFeeRulesComponent),
      },
      {
        path: 'finance/profit-workflow',
        title: 'title.profitWorkflow',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.profitWorkflow,
          permissionMode: FEATURE_PERMISSION_MODES.profitWorkflow,
        },
        loadComponent: () =>
          import('./features/finance/profit-workflow').then((m) => m.ProfitWorkflowComponent),
      },
      {
        path: 'finance/profit',
        pathMatch: 'full',
        redirectTo: 'finance/profit-workflow',
      },
      {
        path: 'staff',
        title: 'title.staff',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.staffDirectory,
          permissionMode: FEATURE_PERMISSION_MODES.staffDirectory,
        },
        loadComponent: () =>
          import('./features/staff/staff-directory').then((m) => m.StaffDirectoryComponent),
      },
      {
        path: 'staff/attendance',
        title: 'title.staffAttendance',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.staffAttendance,
          permissionMode: FEATURE_PERMISSION_MODES.staffAttendance,
        },
        loadComponent: () =>
          import('./features/staff/staff-attendance').then((m) => m.StaffAttendanceComponent),
      },
      {
        path: 'me/attendance',
        title: 'title.myAttendance',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.myAttendance,
          permissionMode: FEATURE_PERMISSION_MODES.myAttendance,
        },
        loadComponent: () =>
          import('./features/staff/my-attendance').then((m) => m.MyAttendanceComponent),
      },
      {
        path: 'staff/attendance-log',
        title: 'title.staffAttendanceLog',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.staffAttendanceLog,
          permissionMode: FEATURE_PERMISSION_MODES.staffAttendanceLog,
        },
        loadComponent: () =>
          import('./features/staff/staff-attendance-log').then((m) => m.StaffAttendanceLogComponent),
      },
      {
        path: 'staff/rounds',
        title: 'title.consultantRounds',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.consultantRounds,
          permissionMode: FEATURE_PERMISSION_MODES.consultantRounds,
        },
        loadComponent: () =>
          import('./features/staff/consultant-rounds').then((m) => m.ConsultantRoundsComponent),
      },
      {
        path: 'staff/doctor-work',
        title: 'title.doctorWorkSheet',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.doctorWorkSheet,
          permissionMode: FEATURE_PERMISSION_MODES.doctorWorkSheet,
        },
        loadComponent: () =>
          import('./features/staff/doctor-work-sheet').then((m) => m.DoctorWorkSheetComponent),
      },
      {
        path: 'examinations',
        title: 'title.examinations',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.examinations,
          permissionMode: FEATURE_PERMISSION_MODES.examinations,
        },
        loadComponent: () =>
          import('./features/staff/doctor-examinations').then((m) => m.DoctorExaminationsComponent),
      },
      {
        path: 'staff/payroll',
        title: 'title.staffPayroll',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.staffPayroll,
          permissionMode: FEATURE_PERMISSION_MODES.staffPayroll,
        },
        loadComponent: () =>
          import('./features/staff/staff-payroll').then((m) => m.StaffPayrollComponent),
      },
      {
        path: 'administration/shift-roster',
        title: 'title.shiftRoster',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.admissionShiftRoster,
          permissionMode: FEATURE_PERMISSION_MODES.admissionShiftRoster,
        },
        loadComponent: () =>
          import('./features/admissions/admission-shift-roster').then(
            (m) => m.AdmissionShiftRosterComponent,
          ),
      },
      {
        path: 'administration/organization',
        title: 'title.organization',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationOrganization,
          permissionMode: FEATURE_PERMISSION_MODES.administrationOrganization,
        },
        loadComponent: () =>
          import('./features/administration/organization/admin-organization').then(
            (m) => m.AdminOrganizationComponent,
          ),
      },
      {
        // «إعدادات النظام» (2026-10-11): the hospital's own settings, for whoever manages the organisation.
        path: 'administration/settings',
        title: 'title.systemSettings',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationSettings,
          permissionMode: FEATURE_PERMISSION_MODES.administrationSettings,
        },
        loadComponent: () =>
          import('./features/administration/settings/system-settings-page').then(
            (m) => m.SystemSettingsPageComponent,
          ),
      },
      {
        // Operations, not administration: the monitor is read by the person
        // keeping a feed alive, who is often not an administrator at all.
        path: 'operations/interfaces',
        title: 'title.interfaceMonitor',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.interfaceMonitor,
          permissionMode: FEATURE_PERMISSION_MODES.interfaceMonitor,
        },
        loadComponent: () =>
          import('./features/interoperability/interface-monitor').then(
            (m) => m.InterfaceMonitorComponent,
          ),
      },
      {
        // Operations, beside the interface monitor: the register is read by a
        // storage custodian or a records officer, and neither is necessarily an
        // administrator.
        path: 'operations/object-custody',
        title: 'title.objectCustody',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.objectCustody,
          permissionMode: FEATURE_PERMISSION_MODES.objectCustody,
        },
        loadComponent: () =>
          import('./features/object-storage/object-custody-register').then(
            (m) => m.ObjectCustodyRegisterComponent,
          ),
      },
      {
        path: 'administration/audit',
        title: 'title.audit',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationAudit,
          permissionMode: FEATURE_PERMISSION_MODES.administrationAudit,
        },
        loadComponent: () =>
          import('./features/administration/audit/admin-audit').then(
            (m) => m.AdminAuditComponent,
          ),
      },
      {
        path: 'administration/audit-trail',
        title: 'title.auditTrail',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationAuditScoped,
          permissionMode: FEATURE_PERMISSION_MODES.administrationAuditScoped,
        },
        loadComponent: () =>
          import('./features/administration/audit/scoped-audit').then(
            (m) => m.ScopedAuditComponent,
          ),
      },
      {
        path: 'administration/facilities',
        title: 'title.facilities',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationFacilities,
          permissionMode: FEATURE_PERMISSION_MODES.administrationFacilities,
        },
        loadComponent: () =>
          import('./features/administration/facilities/admin-facilities').then(
            (m) => m.AdminFacilitiesComponent,
          ),
      },
      {
        path: 'administration/roles',
        title: 'title.roles',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationRoles,
          permissionMode: FEATURE_PERMISSION_MODES.administrationRoles,
        },
        loadComponent: () =>
          import('./features/administration/roles/admin-roles').then(
            (m) => m.AdminRolesComponent,
          ),
      },
      {
        path: 'administration/users',
        title: 'title.users',
        canActivate: [permissionGuard],
        data: {
          permissions: FEATURE_PERMISSIONS.administrationUsers,
          permissionMode: FEATURE_PERMISSION_MODES.administrationUsers,
        },
        loadComponent: () =>
          import('./features/administration/users/admin-users').then(
            (m) => m.AdminUsersComponent,
          ),
      },
    ],
  },
  { path: '**', redirectTo: workspaceLandingRedirect },
];
